const DEFAULT_POSITIONS = {
  certificateDate: { top: "1.77in", left: "6.10in" },
  studentName: { top: "2.75in", left: "2.55in" },
  className: { top: "3.42in", left: "3.66in" },
  sessionStart: { top: "4.13in", left: "0.86in" },
  sessionEnd: { top: "4.13in", left: "1.41in" },
  dob: { top: "4.13in", left: "6.06in" },
  dobInWords: { top: "4.72in", left: "1.41in" },
};

const DEFAULT_PAPER = { width: "7.40in", height: "6.73in" };
const DEFAULT_FONT = { family: "'Kalam', cursive", size: "14px", weight: "600", color: "#1a1a1a" };

function PrintedFormatOverlay({ data, previewMode, templateConfig }) {
  const POS = templateConfig?.positions || DEFAULT_POSITIONS;
  const FONT = {
    family: templateConfig?.fontFamily || DEFAULT_FONT.family,
    fontSize: templateConfig?.fontSize || DEFAULT_FONT.size,
    fontWeight: templateConfig?.fontWeight || DEFAULT_FONT.weight,
    color: templateConfig?.fontColor || DEFAULT_FONT.color,
  };
  const paperWidth = templateConfig?.paperWidth || DEFAULT_PAPER.width;
  const paperHeight = templateConfig?.paperHeight || DEFAULT_PAPER.height;
  const bgImage = templateConfig?.templateUrl || "/bonafide-template.jpg";

  const f = (top, left, extra = {}) => ({
    position: "absolute",
    top: top || "0in",
    left: left || "0in",
    fontFamily: FONT.family,
    fontSize: FONT.fontSize,
    fontWeight: FONT.fontWeight,
    color: FONT.color,
    whiteSpace: "nowrap",
    lineHeight: 1,
    ...extra,
  });

  return (
    <div
      className="print-area"
      style={{
        position: "relative",
        width: paperWidth,
        height: paperHeight,
        overflow: "hidden",
        background: previewMode ? "#fffef5" : "transparent",
        boxShadow: previewMode ? "0 4px 24px rgba(0,0,0,0.18)" : "none",
      }}
    >
      <style>{`@media print { @page { size: ${paperWidth} ${paperHeight}; margin: 0; } }`}</style>

      {previewMode && (
        <img
          src={bgImage}
          alt="template"
          className="certificate-bg-preview"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "contain",
            opacity: 0.85,
            pointerEvents: "none",
          }}
        />
      )}

      <span style={f(POS.certificateDate?.top, POS.certificateDate?.left)}>{data?.certificateDate}</span>
      <span style={f(POS.studentName?.top, POS.studentName?.left, { whiteSpace: "normal", maxWidth: "4.5in" })}>{data?.studentName}</span>
      <span style={f(POS.className?.top, POS.className?.left)}>{data?.className}</span>
      <span style={f(POS.sessionStart?.top, POS.sessionStart?.left)}>{data?.sessionStart}</span>
      <span style={f(POS.sessionEnd?.top, POS.sessionEnd?.left)}>{data?.sessionEnd}</span>
      <span style={f(POS.dob?.top, POS.dob?.left)}>{data?.dob}</span>
      <span style={f(POS.dobInWords?.top, POS.dobInWords?.left, { whiteSpace: "normal", maxWidth: "5.5in" })}>{data?.dobInWords}</span>
    </div>
  );
}

function PlainPaperLayout({ data, college }) {
  return (
    <div
      className="print-area"
      style={{
        position: "relative",
        width: "7.40in",
        height: "6.73in",
        background: "#fff",
        boxSizing: "border-box",
        padding: "0.3in 0.4in",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        border: "2px solid #8B6914",
      }}
    >
      <style>{`@media print { @page { size: 7.40in 6.73in; margin: 0; } }`}</style>

      {/* College Header */}
      <div style={{ textAlign: "center", borderBottom: "2px solid #8B6914", paddingBottom: "4px" }}>
        {college?.sansthaName && (
          <div style={{ fontSize: "8.5px", fontWeight: "700", color: "#666", letterSpacing: "0.5px", textTransform: "uppercase" }}>
            {college.sansthaName}
          </div>
        )}
        <div style={{ fontSize: "16px", fontWeight: "800", color: "#1a1a1a", letterSpacing: "0.8px", margin: "2px 0" }}>
          {college?.name || "CENTRAL INDIA COLLEGE OF PHARMACY"}
        </div>
        <div style={{ fontSize: "9px", fontWeight: "600", color: "#333" }}>
          {college?.address ? `${college.address}, ${college.city} - ${college.pincode}` : "Kh. No. 25/1 B, Mouza-Lonara, Nagpur - 441 111."}
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "8px 0" }}>
        <span style={{ fontSize: "12px", fontWeight: "600" }}>Sr. No.: <strong>{data?.srNo}</strong></span>
        <span style={{ fontSize: "16px", fontWeight: "800", textDecoration: "underline" }}>BONAFIDE CERTIFICATE</span>
        <span style={{ fontSize: "12px" }}>Date: <strong>{data?.certificateDate}</strong></span>
      </div>

      {/* Body Content */}
      <div style={{ fontSize: "13px", lineHeight: "2", color: "#222" }}>
        This is to certify that Shri/Kumari{" "}
        <strong style={{ fontFamily: "'Kalam', cursive", fontSize: "15px", borderBottom: "1px solid #999", padding: "0 8px" }}>
          {data?.studentName}
        </strong>{" "}
        is/was a bonafide student of this college studying in{" "}
        <strong style={{ fontFamily: "'Kalam', cursive", fontSize: "15px", borderBottom: "1px solid #999", padding: "0 8px" }}>
          {data?.className}
        </strong>{" "}
        during the academic session{" "}
        <strong style={{ fontFamily: "'Kalam', cursive", fontSize: "15px", borderBottom: "1px solid #999", padding: "0 4px" }}>
          {data?.sessionStart}
        </strong>{" "}
        –{" "}
        <strong style={{ fontFamily: "'Kalam', cursive", fontSize: "15px", borderBottom: "1px solid #999", padding: "0 4px" }}>
          {data?.sessionEnd}
        </strong>.
        <br />
        As per the college register, his/her date of birth is{" "}
        <strong style={{ fontFamily: "'Kalam', cursive", fontSize: "15px", borderBottom: "1px solid #999", padding: "0 8px" }}>
          {data?.dob}
        </strong>{" "}
        (in words:{" "}
        <em style={{ fontFamily: "'Kalam', cursive", fontSize: "14px", borderBottom: "1px solid #999", padding: "0 6px" }}>
          {data?.dobInWords}
        </em>
        ).
      </div>

      {/* Signatures */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginTop: "1rem" }}>
        <div style={{ textAlign: "center", fontSize: "11px" }}>
          <div style={{ height: "35px" }} />
          <div style={{ borderTop: "1px solid #000", paddingTop: "3px", minWidth: "80px" }}>Clerk</div>
        </div>
        <div style={{ textAlign: "center", fontSize: "11px" }}>
          <div style={{ height: "35px" }} />
          <div style={{ borderTop: "1px solid #000", paddingTop: "3px", minWidth: "120px", fontWeight: "700" }}>PRINCIPAL</div>
        </div>
      </div>
    </div>
  );
}

export default function BonafideCertificate({ data, printMode = "printed", previewMode = false, college, templateConfig }) {
  if (printMode === "plain") return <PlainPaperLayout data={data} college={college} />;
  return <PrintedFormatOverlay data={data} previewMode={previewMode} templateConfig={templateConfig || college?.bonafideTemplate} />;
}